import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AccessModal } from '@/components/AccessModal';
import { SignInModal } from '@/components/SignInModal';

type AuthModalsContextType = {
  openSignIn: () => void;
  openAccess: () => void;
  closeSignIn: () => void;
  closeAccess: () => void;
};

const AuthModalsContext = createContext<AuthModalsContextType | undefined>(undefined);

// Safe-default fallback so consumers rendered briefly outside the provider
// (transitions, error boundaries, lazy chunks) don't crash the whole app.
const NOOP_AUTH_MODALS: AuthModalsContextType = {
  openSignIn: () => {},
  openAccess: () => {},
  closeSignIn: () => {},
  closeAccess: () => {},
};

export const useAuthModals = () => {
  const context = useContext(AuthModalsContext);
  return context ?? NOOP_AUTH_MODALS;
};

export const AuthModalsProvider = ({ children }: { children: ReactNode }) => {
  const [signInOpen, setSignInOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Auto-open sign-in / access modal when redirected from Protected routes or deep links
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let changed = false;
    if (params.get('signin') === 'true') {
      setSignInOpen(true);
      params.delete('signin');
      changed = true;
    }
    if (params.get('access') === 'true') {
      setAccessOpen(true);
      params.delete('access');
      changed = true;
    }
    if (changed) {
      const remaining = params.toString();
      navigate(location.pathname + (remaining ? `?${remaining}` : ''), { replace: true });
    }
  }, [location.search]);

  const openSignIn = () => setSignInOpen(true);
  const openAccess = () => setAccessOpen(true);
  const closeSignIn = () => setSignInOpen(false);
  const closeAccess = () => setAccessOpen(false);

  return (
    <AuthModalsContext.Provider value={{ openSignIn, openAccess, closeSignIn, closeAccess }}>
      {children}
      <SignInModal 
        open={signInOpen} 
        onOpenChange={setSignInOpen}
        onRequestAccess={() => {
          setSignInOpen(false);
          setAccessOpen(true);
        }}
      />
      <AccessModal 
        open={accessOpen} 
        onOpenChange={setAccessOpen}
      />
    </AuthModalsContext.Provider>
  );
};
